// Der Verlauf einer Geräteseite: Punkte 6 bis 12 aus
// docs/design/live-verlauf.md, gestaltet nach docs/design/screens/Verlauf.dc.html.
import { useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { describeMetric, formatDateTime } from '../lib/format'
import type { Sample } from '../lib/types'
import { LineChart, type Markierung, type Punkt, type Series } from '../components/LineChart'
import { Sparkline } from '../components/Sparkline'
import { ErrorBox, Loading } from '../components/Feedback'

/** Ein Zeitraum aus der Leiste. */
export interface Zeitraum {
  key: string
  label: string
  /** Länge in Millisekunden. */
  laenge: number
}

const STUNDE = 3_600_000
const TAG = 24 * STUNDE

// Punkt 6: die sechs angebotenen Zeiträume.
export const ZEITRAEUME: Zeitraum[] = [
  { key: '1h', label: '1 Stunde', laenge: STUNDE },
  { key: '6h', label: '6 Stunden', laenge: 6 * STUNDE },
  { key: '24h', label: '24 Stunden', laenge: TAG },
  { key: '168h', label: '7 Tage', laenge: 7 * TAG },
  { key: '720h', label: '30 Tage', laenge: 30 * TAG },
  // Dreizehn Monate: so lange werden Messwerte aufbewahrt.
  { key: '9360h', label: '13 Monate', laenge: 390 * TAG },
]

/** Was gerade gezeigt wird: ein laufender Zeitraum oder ein fester Ausschnitt. */
export type Sicht =
  | { art: 'zeitraum'; key: string }
  | { art: 'fest'; von: number; bis: number; zurueck: string }

/** Der konkrete Ausschnitt einer Sicht, in Millisekunden. */
export function fenster(sicht: Sicht, jetzt: number): { von: number; bis: number } {
  if (sicht.art === 'fest') return { von: sicht.von, bis: sicht.bis }
  const z = ZEITRAEUME.find((r) => r.key === sicht.key) ?? ZEITRAEUME[1]!
  return { von: jetzt - z.laenge, bis: jetzt }
}

/** Die Abfrageparameter zu einer Sicht. */
export function abfrage(sicht: Sicht): string {
  if (sicht.art === 'zeitraum') return `zeitraum=${sicht.key}`
  return (
    `von=${encodeURIComponent(new Date(sicht.von).toISOString())}` +
    `&bis=${encodeURIComponent(new Date(sicht.bis).toISOString())}`
  )
}

/** Einen Ausschnitt um seine eigene Länge verschieben (Punkt 6, vor/zurück). */
export function verschieben(sicht: Sicht, richtung: -1 | 1, jetzt: number): Sicht {
  const f = fenster(sicht, jetzt)
  const laenge = f.bis - f.von
  const zurueck = sicht.art === 'zeitraum' ? sicht.key : sicht.zurueck
  return {
    art: 'fest',
    von: f.von + richtung * laenge,
    bis: f.bis + richtung * laenge,
    zurueck,
  }
}

/** Für die Eingabefelder: "2026-09-22T09:00" in der Zeitzone des Browsers. */
export function alsEingabe(ms: number): string {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000)
  return d.toISOString().slice(0, 16)
}

/** Was der Verlauf zeigt: eine Metrik, bei Tabellen-Metriken dazu ein Port. */
export interface Wahl {
  metric: string
  port?: string
}

function schluessel(w: Wahl): string {
  return w.port ? `${w.metric}·${w.port}` : w.metric
}

function beschriftung(w: Wahl): string {
  const { label } = describeMetric(w.metric)
  return w.port ? `${label} · ${w.port}` : label
}

/**
 * Die Achse je Messgröße: alles mit der Einheit der ersten Reihe kommt nach
 * links, alles andere nach rechts. Mehr als zwei Einheiten gibt es nicht —
 * dann fehlt der dritten die Achse, und sie wäre nicht lesbar.
 */
export function achsen(wahlen: Wahl[]): ('links' | 'rechts')[] {
  const erste = describeMetric(wahlen[0]?.metric ?? '').unit
  return wahlen.map((w) => (describeMetric(w.metric).unit === erste ? 'links' : 'rechts'))
}

/** Samples in Diagrammpunkte, mit Ausschlägen, wenn sie verdichtet sind. */
export function zuPunkten(samples: Sample[] | undefined): Punkt[] {
  if (!Array.isArray(samples)) return []
  return samples.map((s) => ({
    time: new Date(s.time).getTime(),
    value: s.value,
    ...(s.min !== undefined ? { min: s.min } : {}),
    ...(s.max !== undefined ? { max: s.max } : {}),
  }))
}

interface Props {
  deviceId: string
  /** Die Momentaufnahme, für die Auswahl der Messgrößen und die Detailreiter. */
  latest: Sample[]
  /** Alle Metriken, zu denen es Werte gibt. */
  measured: string[]
  /** Die Ports je Metrik. */
  portsFor: (metric: string) => string[]
  /** Vorauswahl, wenn noch nichts gewählt wurde. */
  vorauswahl: Wahl
  kerne: { index: string }[]
  interfaces: { index: string; name?: string }[]
  dateisysteme: { mount: string }[]
}

export function VerlaufAbschnitt({
  deviceId,
  measured,
  portsFor,
  vorauswahl,
  kerne,
  interfaces,
  dateisysteme,
}: Props) {
  const [sicht, setSicht] = useState<Sicht>({ art: 'zeitraum', key: '6h' })
  const [wahlen, setWahlen] = useState<Wahl[]>([vorauswahl])
  const jetzt = Date.now()
  const f = fenster(sicht, jetzt)
  const parameter = abfrage(sicht)

  const verlaeufe = useQueries({
    queries: wahlen.map((w) => ({
      queryKey: ['verlauf', deviceId, w.metric, w.port ?? '', parameter],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        apiGet<Sample[]>(
          `/devices/${deviceId}/history?metrik=${encodeURIComponent(w.metric)}` +
            (w.port ? `&port=${encodeURIComponent(w.port)}` : '') +
            `&${parameter}`,
          signal,
        ),
      refetchInterval: 60_000,
    })),
  })

  // Punkt 11: Alarme und Wartungsfenster im selben Ausschnitt.
  const markierungen = useQuery({
    queryKey: ['markierungen', deviceId, parameter],
    queryFn: ({ signal }) =>
      apiGet<{ kind: string; title: string; severity?: string; from: string; to?: string }[]>(
        `/devices/${deviceId}/markierungen?${parameter}`,
        signal,
      ),
    refetchInterval: 60_000,
  })

  const achsenliste = achsen(wahlen)
  const series: Series[] = wahlen.map((w, i) => ({
    key: schluessel(w),
    label: beschriftung(w),
    metric: w.metric,
    achse: achsenliste[i],
    points: zuPunkten(verlaeufe[i]?.data),
  }))

  const marker: Markierung[] = (Array.isArray(markierungen.data) ? markierungen.data : []).map((m) => ({
    kind: m.kind === 'wartung' ? 'wartung' : 'alarm',
    title: m.title,
    ...(m.severity ? { severity: m.severity } : {}),
    from: new Date(m.from).getTime(),
    ...(m.to ? { to: new Date(m.to).getTime() } : {}),
  }))

  const laden = verlaeufe.some((q) => q.isPending)
  const fehler = verlaeufe.find((q) => q.isError)
  const proz = describeMetric(wahlen[0]?.metric ?? '').unit === '%'

  const weitere = useMemo(
    () => measured.filter((m) => !wahlen.some((w) => w.metric === m)),
    [measured, wahlen],
  )

  return (
    <section className="card">
      <div className="card-head" style={{ flexWrap: 'wrap', gap: 10 }}>
        <h2 className="card-title">Verlauf</h2>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <div role="group" aria-label="Zeitraum" className="row" style={{ gap: 4 }}>
            {ZEITRAEUME.map((z) => (
              <button
                key={z.key}
                type="button"
                className="btn btn-sm"
                aria-pressed={sicht.art === 'zeitraum' && sicht.key === z.key}
                style={
                  sicht.art === 'zeitraum' && sicht.key === z.key
                    ? { background: 'var(--acc-soft)', color: 'var(--acc)', fontWeight: 600 }
                    : undefined
                }
                onClick={() => setSicht({ art: 'zeitraum', key: z.key })}
              >
                {z.label}
              </button>
            ))}
          </div>
          <label className="sr-only" htmlFor="verlauf-von">
            Von
          </label>
          <input
            id="verlauf-von"
            type="datetime-local"
            className="input"
            style={{ width: 200, height: 28, fontSize: 13 }}
            value={alsEingabe(f.von)}
            onChange={(e) => {
              const von = new Date(e.target.value).getTime()
              if (!Number.isNaN(von) && von < f.bis) {
                setSicht({ art: 'fest', von, bis: f.bis, zurueck: rueckweg(sicht) })
              }
            }}
          />
          <label className="sr-only" htmlFor="verlauf-bis">
            Bis
          </label>
          <input
            id="verlauf-bis"
            type="datetime-local"
            className="input"
            style={{ width: 200, height: 28, fontSize: 13 }}
            value={alsEingabe(f.bis)}
            onChange={(e) => {
              const bis = new Date(e.target.value).getTime()
              if (!Number.isNaN(bis) && bis > f.von) {
                setSicht({ art: 'fest', von: f.von, bis, zurueck: rueckweg(sicht) })
              }
            }}
          />
          <button
            type="button"
            className="btn btn-sm"
            aria-label="Vorheriger Zeitraum"
            onClick={() => setSicht(verschieben(sicht, -1, jetzt))}
          >
            ‹
          </button>
          <button
            type="button"
            className="btn btn-sm"
            aria-label="Nächster Zeitraum"
            onClick={() => setSicht(verschieben(sicht, 1, jetzt))}
          >
            ›
          </button>
          {sicht.art === 'fest' ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setSicht({ art: 'zeitraum', key: sicht.zurueck })}
            >
              Zoom zurücksetzen
            </button>
          ) : null}
        </div>
      </div>

      {/* Punkt 8: mehrere Messgrößen überlagert. */}
      <div className="card-body" style={{ paddingBottom: 0 }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span className="muted" style={{ fontSize: 13 }}>
            Messgrößen
          </span>
          {wahlen.map((w, i) => (
            <span key={schluessel(w)} className="chip">
              <span className="dot" style={{ background: `var(--chart-${i + 1})` }} />
              {beschriftung(w)}
              {wahlen.length > 1 ? (
                <button
                  type="button"
                  className="chip-weg"
                  aria-label={`${beschriftung(w)} entfernen`}
                  onClick={() => setWahlen(wahlen.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
          {weitere.length > 0 && wahlen.length < 4 ? (
            <>
              <label className="sr-only" htmlFor="verlauf-dazu">
                Messgröße hinzufügen
              </label>
              <select
                id="verlauf-dazu"
                className="input"
                style={{ width: 200, height: 28, fontSize: 13 }}
                value=""
                onChange={(e) => {
                  if (!e.target.value) return
                  const ports = portsFor(e.target.value)
                  const erste = ports[0]
                  setWahlen([
                    ...wahlen,
                    erste ? { metric: e.target.value, port: erste } : { metric: e.target.value },
                  ])
                }}
              >
                <option value="">+ Messgröße</option>
                {weitere.map((m) => (
                  <option key={m} value={m}>
                    {describeMetric(m).label}
                  </option>
                ))}
              </select>
            </>
          ) : null}
        </div>
      </div>

      <div className="card-body">
        {laden ? (
          <Loading what="Messwerte" />
        ) : fehler ? (
          <ErrorBox error={fehler.error} what="Die Messwerte" />
        ) : (
          <>
            <LineChart
              metric={wahlen[0]?.metric ?? ''}
              series={series}
              marker={marker}
              height={300}
              minValue={proz ? 0 : undefined}
              maxValue={proz ? 100 : undefined}
              onZoom={(von, bis) =>
                setSicht({ art: 'fest', von, bis, zurueck: rueckweg(sicht) })
              }
            />
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {formatDateTime(new Date(f.von).toISOString())} bis{' '}
              {formatDateTime(new Date(f.bis).toISOString())}
              {marker.length > 0 ? ` · ${marker.length} Markierung(en)` : ''}
            </div>
          </>
        )}
      </div>

      <DetailReiter
        deviceId={deviceId}
        parameter={parameter}
        kerne={kerne}
        interfaces={interfaces}
        dateisysteme={dateisysteme}
      />
    </section>
  )
}

/** Der Zeitraum, zu dem „Zoom zurücksetzen“ zurückführt. */
function rueckweg(sicht: Sicht): string {
  return sicht.art === 'zeitraum' ? sicht.key : sicht.zurueck
}

interface ReiterProps {
  deviceId: string
  parameter: string
  kerne: { index: string }[]
  interfaces: { index: string; name?: string }[]
  dateisysteme: { mount: string }[]
}

/** Punkt 12: je Kern, je Interface, je Dateisystem ein kleiner Graph. */
export function DetailReiter({ deviceId, parameter, kerne, interfaces, dateisysteme }: ReiterProps) {
  const reiter = [
    { key: 'kerne', label: `Je Kern (${kerne.length})`, anzahl: kerne.length },
    { key: 'ports', label: `Je Interface (${interfaces.length})`, anzahl: interfaces.length },
    { key: 'dateisysteme', label: `Je Dateisystem (${dateisysteme.length})`, anzahl: dateisysteme.length },
  ].filter((r) => r.anzahl > 0)

  const [offen, setOffen] = useState(reiter[0]?.key ?? '')
  if (reiter.length === 0) return null
  const gewaehlt = reiter.some((r) => r.key === offen) ? offen : reiter[0]!.key

  return (
    <>
      <div role="tablist" aria-label="Detailansicht" className="reiterleiste">
        {reiter.map((r) => (
          <button
            key={r.key}
            type="button"
            role="tab"
            aria-selected={gewaehlt === r.key}
            className="reiter"
            onClick={() => setOffen(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>
      <div className="mini-gitter">
        {gewaehlt === 'kerne'
          ? kerne.map((k) => (
              <MiniGraph
                key={k.index}
                deviceId={deviceId}
                parameter={parameter}
                metric="cpu_percent"
                label={`Kern ${k.index}`}
              />
            ))
          : null}
        {gewaehlt === 'ports'
          ? interfaces.map((i) => (
              <MiniGraph
                key={i.index}
                deviceId={deviceId}
                parameter={parameter}
                metric="if_in_bits_per_s"
                port={i.name || i.index}
                label={i.name || i.index}
              />
            ))
          : null}
        {gewaehlt === 'dateisysteme'
          ? dateisysteme.map((d) => (
              <MiniGraph
                key={d.mount}
                deviceId={deviceId}
                parameter={parameter}
                metric="filesystem_percent"
                port={d.mount}
                label={d.mount}
              />
            ))
          : null}
      </div>
    </>
  )
}

function MiniGraph({
  deviceId,
  parameter,
  metric,
  port,
  label,
}: {
  deviceId: string
  parameter: string
  metric: string
  port?: string
  label: string
}) {
  const verlauf = useQuery({
    queryKey: ['mini', deviceId, metric, port ?? '', parameter],
    queryFn: ({ signal }) =>
      apiGet<Sample[]>(
        `/devices/${deviceId}/history?metrik=${encodeURIComponent(metric)}` +
          (port ? `&port=${encodeURIComponent(port)}` : '') +
          `&${parameter}`,
        signal,
      ),
    staleTime: 30_000,
  })
  const punkte = zuPunkten(verlauf.data).map((p) => p.value)
  const letzter = punkte.at(-1)

  return (
    <div className="mini">
      <div className="mini-kopf">
        <span className="muted">{label}</span>
        <span className="mono">
          {letzter === undefined ? '–' : describeMetric(metric).unit === '%'
            ? `${Math.round(letzter)} %`
            : Math.round(letzter).toLocaleString('de-AT')}
        </span>
      </div>
      <Sparkline
        points={punkte}
        width={160}
        height={28}
        max={describeMetric(metric).unit === '%' ? 100 : undefined}
        label={`${label}, Verlauf`}
      />
    </div>
  )
}
